/**
 * Follows or unfollows one team from the favorite teams manager
 * (ISSUE-1399), in the same two-service order `useTeamFollow` uses:
 * notification-api's per-team alert subscription first, then profile-api's
 * account-wide `favoriteTeamIds`. A failed first write stops there, so the
 * two can never disagree in the direction that leaves a fan getting alerts
 * for a team they removed.
 *
 * Unlike `useTeamFollow`, the direction is the caller's, not read off a
 * per-team settings request: the manager already knows from `/profile/get`
 * whether each row is followed, and a list of thirty teams should not fire
 * thirty reads to find out again.
 *
 * `pendingTeamId` is the one row in flight, so only the tapped control
 * shows a spinner (`scoped-loading-state.mdc`).
 */
import { useNotificationSettingsSetTeamFollow } from "@sportsmet/notification-sdk/private";
import {
  useTeamFollowAdd,
  useTeamFollowRemove,
} from "@sportsmet/profile-sdk/private";
import { useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";

export type typeFavoriteTeamWrite = {
  pendingTeamId: string | null;
  setFollowing: (teamId: string, isFollowing: boolean) => Promise<void>;
};

export function useFavoriteTeamWrite(): typeFavoriteTeamWrite {
  const setTeamFollow = useNotificationSettingsSetTeamFollow();
  const teamFollowAdd = useTeamFollowAdd();
  const teamFollowRemove = useTeamFollowRemove();
  const [pendingTeamId, setPendingTeamId] = useState<string | null>(null);

  async function setFollowing(
    teamId: string,
    isFollowing: boolean,
  ): Promise<void> {
    if (pendingTeamId !== null) return;
    setPendingTeamId(teamId);
    try {
      const alerts = await setTeamFollow.mutateAsync({ teamId, isFollowing });
      if (showApiError(alerts)) return;
      const list = isFollowing
        ? await teamFollowAdd.mutateAsync({ teamId })
        : await teamFollowRemove.mutateAsync({ teamId });
      showApiError(list);
    } catch {
      showOfflineError();
    } finally {
      setPendingTeamId(null);
    }
  }

  return { pendingTeamId, setFollowing };
}
